import React, { useState, useEffect, useCallback } from 'react';
import SMSLayout from '../components/SMSLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, Search, Plus, Trash2, Upload, Download, RefreshCw, 
  Phone, Calendar, X, Loader2, FileSpreadsheet, Edit,
  ChevronLeft, ChevronRight, Tag, MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SMSContacts = () => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    withPhone: 0,
    groups: 0
  });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Groups
  const [groups, setGroups] = useState([]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      // Use data-import saved files as contact lists
      const response = await axios.get(`${API_URL}/data-import/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const files = response.data || [];
      
      // Create virtual contacts from saved files
      let allContacts = [];
      files.forEach(file => {
        allContacts.push({
          id: file.id,
          name: file.name,
          type: 'file',
          count: file.total_numbers || file.contacts_count || 0,
          created_at: file.created_at,
          source: 'data-import'
        });
      });
      
      // Filter by search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        allContacts = allContacts.filter(c => 
          c.name?.toLowerCase().includes(query)
        );
      }
      
      setContacts(allContacts);
      setTotal(allContacts.length);
      setTotalPages(Math.ceil(allContacts.length / 20));
      setStats({
        total: allContacts.length,
        withPhone: allContacts.reduce((sum, c) => sum + (c.count || 0), 0),
        groups: 0
      });
      
    } catch (error) {
      console.error('Error fetching contacts:', error);
      // Fallback - show empty state
      setContacts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette liste de contacts ?')) return;
    
    try {
      await axios.delete(`${API_URL}/data-import/files/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Liste supprimée');
      fetchContacts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExport = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/data-import/export/${id}?format=csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contacts_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export terminé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <SMSLayout>
      <div className="space-y-6" data-testid="sms-contacts-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Contacts SMS
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Gérez vos listes de contacts pour les campagnes SMS
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={fetchContacts}
              disabled={loading}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,18%)]' : ''}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              onClick={() => window.location.href = '/sms/data-import'}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              Importer des contacts
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                  <FileSpreadsheet className={`h-5 w-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats.total}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Listes de contacts
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                  <Phone className={`h-5 w-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats.withPhone.toLocaleString()}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Numéros de téléphone
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                  <Tag className={`h-5 w-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats.groups}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Groupes
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  <Input
                    placeholder="Rechercher une liste..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-9 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                </div>
              </div>
              
              {searchQuery && (
                <Button
                  variant="ghost"
                  onClick={() => setSearchQuery('')}
                  className={isDarkMode ? 'text-slate-400 hover:text-white' : ''}
                >
                  <X className="h-4 w-4 mr-2" />
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contacts List */}
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
              Listes de contacts
              {total > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {total} liste{total > 1 ? 's' : ''}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : contacts.length === 0 ? (
              <div className={`text-center py-12 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">Aucune liste de contacts</p>
                <p className="text-sm mt-1 mb-4">Importez vos contacts pour créer des campagnes SMS</p>
                <Button
                  onClick={() => window.location.href = '/sms/data-import'}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Importer des contacts
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className={`flex items-center justify-between p-4 rounded-xl ${
                      isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                        <FileSpreadsheet className={`h-5 w-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                      </div>
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {contact.name}
                        </p>
                        <div className={`flex items-center gap-3 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.count?.toLocaleString() || 0} contacts
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(contact.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={isDarkMode ? 'border-slate-600 text-slate-400' : ''}>
                        {contact.source}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={isDarkMode ? 'text-slate-400 hover:text-white' : ''}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => handleExport(contact.id)}>
                            <Download className="h-4 w-4 mr-2" />
                            Exporter
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(contact.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`flex items-center justify-between mt-6 pt-4 border-t ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : 'border-slate-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Page {page} sur {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SMSLayout>
  );
};

export default SMSContacts;
